import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { useAdCampaignMerchantConfiguration } from 'modules/campaigns/ad-campaign/hooks/useAdCampaignMerchantConfiguration';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { MerchantAdCampaignConfigFormValues, MerchantAdCampaignFormValues } from 'modules/campaigns/types';

import { useCreateMerchantAdCampaign } from './useCreateMerchantAdCampaign';

const getAdFormDefaultValues = ({
  adConfig,
  venueIds,
}: {
  adConfig: MerchantAdCampaignConfigFormValues | undefined;
  venueIds: string[];
}): MerchantAdCampaignFormValues => ({
  campaignType: CampaignType.ADS,
  agreementTermsAndConditions: false,
  budgetAmount: adConfig?.budgetOptions.find(o => o.type === 'recommended')?.value ?? 0,
  startDate: DateTime.local().toISO(),
  budgetType: 'recommended',
  bidStrategy: 'auto',
  scheduleStrategy: 'alwaysOn',
  isFreeTrial: false,
  venueIds,
});

export const useMerchantAdCampaignFormValues = ({ isEnabled }: { isEnabled: boolean }) => {
  const { merchantId, country, venues } = useCampaignModuleContext();

  const createMerchantAdCampaign = useCreateMerchantAdCampaign();

  const handleSubmitAdCampaign = (values: MerchantAdCampaignFormValues) => {
    return createMerchantAdCampaign.mutate(values);
  };

  const { data: merchantAdsConfig, isLoading: isAdConfigLoading } = useAdCampaignMerchantConfiguration({
    merchantId,
    country,
    enabled: isEnabled,
  });

  const defaultValues = useMemo(
    () =>
      getAdFormDefaultValues({
        adConfig: merchantAdsConfig,
        // todo(mattlemmone): Update default selected venues when venue picker logic is defined
        venueIds: venues.map(v => v.id),
      }),
    [merchantAdsConfig, venues],
  );

  return {
    adConfig: merchantAdsConfig,
    freeTrialConfig: undefined,
    defaultValues,
    submitForm: handleSubmitAdCampaign,
    isLoading: isAdConfigLoading,
    isFormSubmissionLoading: createMerchantAdCampaign.isLoading,
  };
};
